import { useAuthStore } from '@/store/authStore';

export type Role = 'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'RECRUITER' | 'HR' | 'HIRING_MANAGER' | 'INTERVIEWER' | 'CANDIDATE';

export type Capability =
  | 'manageTeam'          // add/remove teammates, change roles
  | 'manageAssessments'   // create/edit jobs, assessments, invite candidates
  | 'manageLibrary'       // create/edit tests & questions
  | 'inviteCandidates'
  | 'viewResults'         // read candidate results & analytics
  | 'manageSettings'      // org branding, security, billing
  | 'addInterviewers';    // add Interviewer accounts (owner's decision 15, with "Team rules" on)

const ALL: Capability[] = ['manageTeam', 'manageAssessments', 'manageLibrary', 'inviteCandidates', 'viewResults', 'manageSettings', 'addInterviewers'];
const CREATOR: Capability[] = ['manageAssessments', 'manageLibrary', 'inviteCandidates', 'viewResults', 'addInterviewers'];
const VIEWER: Capability[] = ['viewResults'];

/**
 * Maps each role (incl. legacy SUPER_ADMIN/HR) to its capabilities.
 *
 * INTERVIEWER has none on purpose. It sees only the interviews it is asked to join, through
 * one allow-list of addresses (lib/roleRouting.ts) rather than through capabilities, so no
 * capability added later can open a staff page to it by accident. The server is the real
 * lock: every staff route answers an Interviewer 'not found'.
 */
const roleCaps: Record<Role, Capability[]> = {
  OWNER: ALL,
  SUPER_ADMIN: ALL,
  ADMIN: ALL,
  RECRUITER: CREATOR,
  HR: CREATOR,          // legacy alias of Recruiter
  HIRING_MANAGER: VIEWER,
  INTERVIEWER: [],
  CANDIDATE: [],
};

/** Human-facing labels, collapsing legacy roles onto the TestGorilla set. */
export const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  SUPER_ADMIN: 'Owner',
  ADMIN: 'Admin',
  RECRUITER: 'Recruiter',
  HR: 'Recruiter',
  HIRING_MANAGER: 'Hiring Manager',
  INTERVIEWER: 'Interviewer',
  CANDIDATE: 'Candidate',
};

/** Roles assignable from the Team UI, as it has always offered them. */
export const ASSIGNABLE_ROLES: { value: Role; label: string; hint: string }[] = [
  { value: 'ADMIN', label: 'Admin', hint: 'Full access to everything, including team & settings' },
  { value: 'RECRUITER', label: 'Recruiter', hint: 'Create assessments, invite candidates, view results' },
  { value: 'HIRING_MANAGER', label: 'Hiring Manager', hint: 'Review candidate results and leave feedback (read-only)' },
];

/** The Interviewer role, offered only with "Team rules" on (the server refuses it otherwise). */
export const INTERVIEWER_ROLE = {
  value: 'INTERVIEWER' as Role,
  label: 'Interviewer',
  hint: 'Sees only the interviews they are asked to join',
};

/**
 * The roles the Team page offers.
 *
 * With "Team rules" off: exactly today's three. With it on, the hints are honest (the plan's
 * "the hints are honest"): nothing in CogniTest lets a Hiring Manager "leave feedback" until
 * the interview scorecards exist, which arrive with Interviews; and Interviewer is offered,
 * because only then does the server accept it — with the email-domain rule, the
 * set-password email and the record that come with it.
 */
export function assignableRoles(opts: { teamRules: boolean; interviews: boolean }): { value: Role; label: string; hint: string }[] {
  if (!opts.teamRules) return ASSIGNABLE_ROLES;
  return [
    ...ASSIGNABLE_ROLES.map((r) => (r.value === 'HIRING_MANAGER'
      ? {
        ...r,
        hint: opts.interviews
          ? 'Reads candidates and results; can sit on interview panels and write scorecards'
          : 'Reads candidates and results (read-only)',
      }
      : r)),
    INTERVIEWER_ROLE,
  ];
}

export function capsForRole(role?: string): Capability[] {
  return roleCaps[(role as Role)] ?? [];
}

export function roleLabel(role?: string): string {
  return ROLE_LABELS[role ?? ''] ?? role ?? '—';
}

/** The Owner and Admin roles — the server's MANAGERS group (backend utils/roles.ts). */
export const MANAGER_ROLES: readonly string[] = ['OWNER', 'SUPER_ADMIN', 'ADMIN'];
export const isManagerRole = (role?: string | null): boolean => MANAGER_ROLES.includes(role ?? '');

/**
 * The company's Owner — the server's OWNER_ROLES (backend utils/roles.ts). A legacy
 * SUPER_ADMIN is labelled "Owner" everywhere (ROLE_LABELS), so it is one wherever the Owner
 * alone may act: the Switches tab, and the Owner's say over Owners on the Team page.
 */
export const OWNER_ROLES: readonly string[] = ['OWNER', 'SUPER_ADMIN'];
export const isOwnerRole = (role?: string | null): boolean => OWNER_ROLES.includes(role ?? '');

/** Hook exposing the current user's role + capability checks. */
export function usePermissions() {
  const role = useAuthStore((s) => s.user?.role);
  const caps = capsForRole(role);
  return {
    role,
    caps,
    can: (c: Capability) => caps.includes(c),
  };
}
